import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../api/client'
import {
  CASE_STATUS,
  caseTone,
  markVendorReplied,
  setVendorCaseReference,
  setVendorCaseStatus,
  type TicketVendorCases as CasesPage,
  type VendorCase,
} from '../api/vendorCases'
import { toast } from '../ui/notify'
import './TicketVendorCases.css'

/**
 * Cazurile la furnizor, in banda aplicatiilor de pe tichet.
 *
 * Fiecare caz: starea (cu mutarile permise), numarul cazului la furnizor,
 * nivelul, termenul primului raspuns (rosu cand a trecut fara raspuns),
 * "Vendor replied" si "Escalate to level N" (contactul de pe nivelul urmator,
 * prin aceeasi fereastra de email). La Closed / Resolved, daca tichetul a fost
 * pus de caz in Waiting for Third Party, o bifa il readuce.
 */

const TITLE = 'Vendor case'

function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'The vendor case could not be changed.'
}

export function TicketVendorCases({
  ticketId,
  page,
  onEscalateNext,
}: {
  ticketId: number
  page: CasesPage
  onEscalateNext(c: VendorCase): void
}) {
  if (!page.ready || page.cases.length === 0) return null
  return (
    <>
      {page.cases.map((c) => (
        <CaseRow key={c.id} ticketId={ticketId} item={c} canEdit={page.canEdit === true} onEscalateNext={() => onEscalateNext(c)} />
      ))}
    </>
  )
}

function CaseRow({ ticketId, item, canEdit, onEscalateNext }: { ticketId: number; item: VendorCase; canEdit: boolean; onEscalateNext(): void }) {
  const queryClient = useQueryClient()
  const [editingRef, setEditingRef] = useState(false)
  const [ref, setRef] = useState(item.vendorRef)
  const [restore, setRestore] = useState(true)
  const [showHistory, setShowHistory] = useState(false)

  const done = (message: string) => {
    toast(message, TITLE)
    void queryClient.invalidateQueries({ queryKey: ['business-apps', 'vendor-cases'] })
    void queryClient.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticketId] })
  }
  const move = useMutation({
    mutationFn: (status: number) => setVendorCaseStatus(item.id, status, restore),
    onSuccess: (r) => done(r.message),
    onError: (e) => toast(failed(e), TITLE, 'error'),
  })
  const saveRef = useMutation({
    mutationFn: () => setVendorCaseReference(item.id, ref),
    onSuccess: (r) => {
      setEditingRef(false)
      done(r.message)
    },
    onError: (e) => toast(failed(e), TITLE, 'error'),
  })
  const replied = useMutation({
    mutationFn: () => markVendorReplied(item.id),
    onSuccess: (r) => done(r.message),
    onError: (e) => toast(failed(e), TITLE, 'error'),
  })

  const busy = move.isPending || saveRef.isPending || replied.isPending
  const closed = item.status === CASE_STATUS.CLOSED
  const ending = (item.transitions ?? []).some((t) => t.value === CASE_STATUS.CLOSED || t.value === CASE_STATUS.RESOLVED)

  return (
    <div className={`tk-vendor-row tk-vcase${item.overdue ? ' tk-vendor-overdue' : ''}`} data-case={item.id}>
      <span className="tk-vendor-label">Vendor case</span>
      <div className="tk-vcase-body">
        <div className="tk-vcase-line">
          <span className={`label ${caseTone(item)} tk-vcase-status`}>{item.statusLabel}</span>
          <strong>{item.supportName}</strong>
          <span className="text-muted">
            {item.appName}
            {item.level > 0 ? ` · level ${item.level}` : ''}
          </span>
          {editingRef ? (
            <span className="tk-vcase-ref-edit">
              <input
                className="form-control input-sm tk-vcase-ref-input"
                aria-label="Vendor case number"
                placeholder="e.g. TS-12345"
                value={ref}
                maxLength={100}
                onChange={(ev) => setRef(ev.target.value)}
              />
              <button type="button" className="btn btn-xs btn-primary" disabled={busy} onClick={() => saveRef.mutate()}>
                Save
              </button>
              <button type="button" className="btn btn-xs btn-default" onClick={() => setEditingRef(false)}>
                Cancel
              </button>
            </span>
          ) : (
            <span className="tk-vcase-ref">
              {item.vendorRef !== '' ? (
                <>
                  Case <strong>{item.vendorRef}</strong>
                </>
              ) : (
                <span className="text-muted">no vendor case number</span>
              )}
              {canEdit && !closed && (
                <button type="button" className="btn btn-link btn-xs tk-vcase-ref-btn" onClick={() => setEditingRef(true)}>
                  {item.vendorRef !== '' ? 'edit' : 'add'}
                </button>
              )}
            </span>
          )}
        </div>

        <div className="tk-vcase-line tk-vcase-due">
          {item.awaitingResponse ? (
            item.dueAt !== null ? (
              <>
                First response due <strong>{item.dueAt}</strong>
                {item.overdue && <span className="label label-danger tk-vendor-late">overdue</span>}
              </>
            ) : (
              <span className="text-muted">waiting for an answer - no response time in the contract</span>
            )
          ) : item.respondedAt !== null ? (
            <span>
              Vendor answered <strong>{item.respondedAt}</strong>
              {item.dueAt !== null && item.respondedAt > item.dueAt && <span className="text-muted"> (after the {item.dueAt} due date)</span>}
            </span>
          ) : (
            <span className="text-muted">{item.closedAt !== null ? `closed ${item.closedAt}` : ''}</span>
          )}
          <span className="text-muted"> · sent {item.sentAt}</span>
          {item.ticketWaiting && <span className="label label-default tk-vcase-waiting">ticket waiting for third party</span>}
        </div>

        {canEdit && !closed && (
          <div className="tk-vcase-actions">
            {item.awaitingResponse && (
              <button type="button" className="btn btn-xs btn-default tk-vcase-replied" disabled={busy} onClick={() => replied.mutate()}>
                <i className="fas fa-reply" aria-hidden="true"></i> Vendor replied
              </button>
            )}
            {item.nextLevel && (
              <button
                type="button"
                className={`btn btn-xs ${item.overdue ? 'btn-danger' : 'btn-warning'} tk-vcase-next`}
                disabled={busy}
                onClick={onEscalateNext}
                title={item.nextLevel.levelNote !== '' ? item.nextLevel.levelNote : undefined}
              >
                <i className="fas fa-level-up-alt" aria-hidden="true"></i> Escalate to level {item.nextLevel.level} ({item.nextLevel.name})
              </button>
            )}
            {(item.transitions ?? []).length > 0 && (
              <select
                className="form-control input-sm tk-vcase-move"
                aria-label="Move the vendor case to"
                value=""
                disabled={busy}
                onChange={(ev) => {
                  const next = Number(ev.target.value)
                  if (next > 0) move.mutate(next)
                }}
              >
                <option value="">Set status...</option>
                {(item.transitions ?? []).map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            )}
            {item.ticketWaiting && ending && (
              <label className="tk-vcase-restore">
                <input type="checkbox" checked={restore} onChange={(ev) => setRestore(ev.target.checked)} /> on Resolved / Closed, take the ticket out of
                Waiting for Third Party
              </label>
            )}
          </div>
        )}

        {(item.history ?? []).length > 0 && (
          <button type="button" className="btn btn-link btn-xs tk-vcase-history-btn" onClick={() => setShowHistory((s) => !s)}>
            {showHistory ? 'Hide history' : `History (${(item.history ?? []).length})`}
          </button>
        )}
        {showHistory && (
          <ul className="tk-vcase-history">
            {(item.history ?? []).map((h, i) => (
              <li key={i}>
                <span className="text-muted">{h.at}</span> {h.text}
                {h.by !== '' && <span className="text-muted"> · {h.by}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
